import { cn } from '@/lib/utils';
import {
  buildPhoneE164,
  DEFAULT_COUNTRY,
  formatNationalInput,
  getCountryOptions,
  parsePhoneFields,
} from '@/utils/phoneUtils';
import { CountryCode } from 'libphonenumber-js';
import { useEffect, useMemo, useState } from 'react';

interface AppPhoneInputProps {
  value?: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  id?: string;
  placeholder?: string;
  disabled?: boolean;
  defaultCountry?: CountryCode;
  className?: string;
}

export default function AppPhoneInput({
  value = '',
  onChange,
  onBlur,
  id,
  placeholder = 'Phone number',
  disabled = false,
  defaultCountry = DEFAULT_COUNTRY,
  className,
}: AppPhoneInputProps) {
  const countryOptions = useMemo(() => getCountryOptions(), []);
  const initialFields = useMemo(() => parsePhoneFields(value, defaultCountry), [value, defaultCountry]);

  const [country, setCountry] = useState<CountryCode>(initialFields.country);
  const [nationalInput, setNationalInput] = useState(() =>
    formatNationalInput(initialFields.country, initialFields.nationalNumber)
  );

  useEffect(() => {
    const nextFields = parsePhoneFields(value, defaultCountry);
    setCountry(nextFields.country);
    setNationalInput(formatNationalInput(nextFields.country, nextFields.nationalNumber));
  }, [value, defaultCountry]);

  const handleCountryChange = (nextCountry: CountryCode) => {
    setCountry(nextCountry);
    const digits = nationalInput.replace(/\D/g, '');
    const formatted = formatNationalInput(nextCountry, digits);
    setNationalInput(formatted);
    onChange(buildPhoneE164(nextCountry, digits));
  };

  const handleNationalChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatNationalInput(country, event.target.value);
    setNationalInput(formatted);
    onChange(buildPhoneE164(country, formatted));
  };

  return (
    <div
      className={cn(
        'flex items-center gap-2 rounded-lg border border-coal-300 bg-white px-3 transition-all duration-200 hover:border-coal-400 focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/15',
        disabled && 'cursor-not-allowed opacity-60',
        className
      )}
    >
      <select
        value={country}
        disabled={disabled}
        onChange={(event) => handleCountryChange(event.target.value as CountryCode)}
        className="h-9 max-w-[7.5rem] flex-shrink-0 cursor-pointer border-0 bg-transparent pr-1 text-sm text-coal-900 focus:outline-none"
        aria-label="Country code"
      >
        {countryOptions.map((option) => (
          <option key={option.code} value={option.code}>
            {option.flag} +{option.callingCode} {option.name}
          </option>
        ))}
      </select>

      <div className="h-5 w-px flex-shrink-0 bg-coal-200" />

      <input
        id={id}
        type="tel"
        inputMode="tel"
        autoComplete="tel-national"
        disabled={disabled}
        value={nationalInput}
        onChange={handleNationalChange}
        onBlur={onBlur}
        placeholder={placeholder}
        className="min-h-9 w-full flex-1 bg-transparent text-sm text-coal-900 placeholder:text-coal-400 focus:outline-none"
      />
    </div>
  );
}
